import { useCallback } from 'react';
import CheckableSelect from 'components/CheckableSelect/CheckableSelect';
import dayjs, { type Dayjs } from 'dayjs';
import { capitalize, debounce } from 'lodash';
import {
  availabilityCollectionTimeslotsSelectedTimeslotSelector,
  updatedSelectedTimeslot,
} from 'slices/availabilityCollectionTimeslot.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import type { TAvailabilityCollectionTimeslot } from 'types/availabilityCollectionTimeslot.type';
import { EWeekdays } from '@timeedit/types/lib/enums';
import { TEDrawerSection, TEInput } from '@timeedit/ui-components';
import { Space } from '@timeedit/ui-components/antd';

const weekdayOptions = Object.keys(EWeekdays).map((key) => ({
  value: key,
  label: capitalize(key),
}));

const WEEKDAYS_MAP = {
  [EWeekdays.MON]: 0,
  [EWeekdays.TUE]: 1,
  [EWeekdays.WED]: 2,
  [EWeekdays.THU]: 3,
  [EWeekdays.FRI]: 4,
  [EWeekdays.SAT]: 5,
  [EWeekdays.SUN]: 6,
};

const WEEKDAYS_KEYS = Object.keys(EWeekdays);

const convertTimeToMinutes = (time: Dayjs): number => {
  return time.hour() * 60 + time.minute();
};

type TimeslotDrawerProps = {};

const TimeslotDetail = (props: TimeslotDrawerProps) => {
  const dispatch = useAppDispatch();
  const selectedTimeslot = useAppSelector(
    availabilityCollectionTimeslotsSelectedTimeslotSelector
  );

  const onChangeDebounce = debounce(
    (partialObject: Partial<TAvailabilityCollectionTimeslot>) => {
      dispatch(updatedSelectedTimeslot(partialObject));
    },
    400
  );

  const onChangeTimeInput = useCallback(
    (field: string, value: Dayjs) => {
      const minutes = convertTimeToMinutes(value);
      dispatch(updatedSelectedTimeslot({ [field]: minutes }));
    },
    [dispatch]
  );

  const onChangeWeekdays = useCallback(
    (value: EWeekdays[]) => {
      const weekdays = value.map((day) => WEEKDAYS_MAP[day]);
      dispatch(updatedSelectedTimeslot({ weekdays }));
    },
    [dispatch]
  );

  return (
    <>
      <TEDrawerSection label="General">
        <Space direction="vertical" style={{ width: '100%' }}>
          <TEInput.Input
            value={selectedTimeslot?.name as string}
            label="Name"
            onKeyUp={(e) => onChangeDebounce({ name: e.currentTarget.value })}
            tooltip="Preferred name for the timeslot"
          />
          <TEInput.TextArea
            value={selectedTimeslot?.description as string}
            label="Description"
            onKeyUp={(e) =>
              onChangeDebounce({ description: e.currentTarget.value })
            }
            tooltip="Description"
          />
        </Space>
      </TEDrawerSection>
      <TEDrawerSection label="Detail">
        <Space direction="vertical">
          <TEInput.TimePicker
            format="HH:mm"
            label="Start Time"
            value={dayjs()
              .startOf('day')
              .add(selectedTimeslot?.startTime, 'minute')}
            onChange={(time) => onChangeTimeInput('startTime', time)}
            tooltip="Start time of the timeslot"
          />
          <TEInput.TimePicker
            format="HH:mm"
            label="End Time"
            value={dayjs()
              .startOf('day')
              .add(selectedTimeslot?.endTime, 'minute')}
            onChange={(time) => onChangeTimeInput('endTime', time)}
            tooltip="End time of the timeslot"
          />
          <CheckableSelect
            label="Select weekday(s)"
            tooltip="Select weekday(s)"
            options={weekdayOptions}
            value={
              selectedTimeslot?.weekdays?.map((day) => WEEKDAYS_KEYS[day]) || []
            }
            onChange={onChangeWeekdays}
            highlightValue={[EWeekdays.SUN]}
          />
        </Space>
      </TEDrawerSection>
    </>
  );
};

export default TimeslotDetail;
